import { FC, useEffect, useMemo, useState } from 'react'

import DropdownBoldArrowIcon from 'src/components/ui/atoms/Icons/DropdownBoldArrowIcon'
import InputCompletion from 'src/components/ui/atoms/InputCompletion'
import PanelInfo from 'src/components/ui/atoms/PanelInfo'
import SidebarMenuItem from 'src/components/ui/atoms/SidebarMenuItem'
import { SidebarMenuItemContext } from 'src/components/ui/atoms/SidebarMenuItem/SidebarMenuItemProvider'
import { useId } from 'src/hooks/useId'
import { cn } from 'src/lib/cn'
import useSelectedDashboardStore from 'src/stores/useSelectedDashboardStore'
import useSidebarStore from 'src/stores/useSidebarStore'
import { AppMenuCountryInterface, AppMenuSavedDashboardInterface } from 'src/types/AppMenuInterface'
import { isTrueOrUndefinedFn } from 'src/utils/booleanUtils'
import { SIDEBAR_MENU_HOVER_ENTER_DELAY } from 'src/utils/constants/SidebarMenu'
import SidebarGroupedCountries from './SidebarGroupedCountries'
import SidebarProductDashboard from './SidebarProductDashboard'

interface SidebarProductCustomDashboardsProps {
  dashboards: AppMenuSavedDashboardInterface[]
  subProductCode: string
  link: string
  isVisible?: boolean
  label?: string
  groupedCountries?: AppMenuCountryInterface[]
  className?: string
}

const SidebarProductCustomDashboards: FC<SidebarProductCustomDashboardsProps> = ({
  dashboards,
  subProductCode,
  link,
  isVisible,
  label = 'Custom Dashboards',
  groupedCountries,
  className,
}) => {
  const selectedDashboard = useSelectedDashboardStore(s => s.selectedDashboard)
  const hasSelectedDashboard = dashboards.some(dashboard => dashboard.id === selectedDashboard?.id)

  const { toggle: toggleSidebar, searchInputFocusedId, setSearchInputFocusedId } = useSidebarStore()

  const [search, setSearch] = useState('')

  const id = useId()

  useEffect(() => {
    if (isVisible) {
      setSearch('')
      setSearchInputFocusedId(null)
    }
  }, [isVisible, setSearchInputFocusedId])

  const filteredDashboards = useMemo(() => {
    return dashboards.filter(dashboard =>
      dashboard.name.toLowerCase().includes(search.toLowerCase()),
    )
  }, [dashboards, search])

  const otherDashboards = filteredDashboards?.filter(dashboard =>
    groupedCountries?.length ? !dashboard.program_id : true,
  )

  return (
    <SidebarMenuItem
      label={label}
      active={hasSelectedDashboard}
      onClick={() => {}}
      className={cn('pl-12', className)}
      rightIcon={<DropdownBoldArrowIcon direction="right" className="text-hit-gray" />}
      isVisible={isVisible}
      menu={{
        isOpen: Boolean(isVisible && searchInputFocusedId && searchInputFocusedId === id),
        openOnHover: true,
        delay: SIDEBAR_MENU_HOVER_ENTER_DELAY,
        className: 'py-0',
        content: (
          <SidebarMenuItemContext.Consumer>
            {contextProps => (
              <div className="min-h-37">
                <div className="sticky top-0 z-1 bg-white pt-2 pb-1">
                  <InputCompletion
                    inputValue={search}
                    onClear={() => setSearch('')}
                    placeHolder="Search"
                    onChange={e => setSearch(e.target.value)}
                    onFocus={() => setSearchInputFocusedId(id)}
                    onBlur={() => {
                      setSearchInputFocusedId(null)
                      toggleSidebar(false)
                    }}
                  />
                </div>
                <div className="mt-1">
                  {!filteredDashboards.length && <PanelInfo label="No matching results" />}
                  {otherDashboards?.map(dashboard => (
                    <SidebarProductDashboard
                      key={dashboard.id}
                      dashboard={dashboard}
                      subProductCode={subProductCode}
                      programId={dashboard.program_id}
                      link={link}
                      onClick={() => {
                        toggleSidebar(false)
                        contextProps?.closeHover()
                      }}
                      className="h-[25px] min-w-auto pl-4"
                      hasAccess={isTrueOrUndefinedFn(dashboard?.has_access)}
                      rightIconClassName="ml-auto!"
                      isOnModal
                    />
                  ))}
                  {Boolean(groupedCountries?.length) && (
                    <SidebarGroupedCountries
                      subProductCode={subProductCode}
                      link={link}
                      countries={groupedCountries as AppMenuCountryInterface[]}
                      dashboards={filteredDashboards}
                      onClick={() => {
                        toggleSidebar(false)
                        contextProps?.closeHover()
                      }}
                    />
                  )}
                </div>
              </div>
            )}
          </SidebarMenuItemContext.Consumer>
        ),
      }}
    />
  )
}

export default SidebarProductCustomDashboards
